import React, { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import io from 'socket.io-client';
import { API_URL, apiGet, apiPatch, apiPost } from '../api';
import { formatNumberPrecise } from '../Utils';
import DailyHistoryViewer from './TeenPattiame/DailyHistoryViewer';
import './Admin.css';

// Game runs in zoneserver (/trustFish namespace); settings/stats via /api/games.
const GAME_KEY = 'trustFish';
const SOCKET_SERVER_URL = `${API_URL}/${GAME_KEY}`;

const BIAS_OPTIONS = [-30, -15, -10, -5, 0, 5, 10, 15, 20];

const TrustFish = () => {
    const [isClockRunning, setIsClockRunning] = useState(false);
    const [gamePhase, setGamePhase] = useState('N/A');
    const [roundId, setRoundId] = useState(0);
    const [countdown, setCountdown] = useState(0);
    const [phaseStartTime, setPhaseStartTime] = useState(null);
    const [phaseDuration, setPhaseDuration] = useState(0);
    const [appStats, setAppStats] = useState({ totalProfit: 0 });
    const [winningHistory, setWinningHistory] = useState([]);
    const [lastWinnerInfo, setLastWinnerInfo] = useState(null);
    const [pots, setPots] = useState({ A: 0, B: 0, C: 0 });
    const [selectedBias, setSelectedBias] = useState(0);
    const [sectionLimit, setSectionLimit] = useState(100000);
    const [loading, setLoading] = useState(false);
    const [biasLoading, setBiasLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const socketRef = useRef(null);
    const animationFrameId = useRef(null);

    useEffect(() => {
        let isMounted = true;
        const initAdminSocket = async () => {
            const auth = getAuth();
            const token = await auth.currentUser?.getIdToken();
            if (!token || !isMounted) return;
            socketRef.current = io(SOCKET_SERVER_URL, { transports: ['websocket'], auth: { token } });
            socketRef.current.on('gameStateUpdate', (data) => {
                if (data.gamePhase) setGamePhase(data.gamePhase);
                if (data.roundId) setRoundId(data.roundId);
                if (data.pots) setPots(data.pots);
                if (data.timerInfo) { setPhaseStartTime(data.timerInfo.phaseStartTime); setPhaseDuration(data.timerInfo.phaseDuration); }
                if (data.lastWinnerInfo !== undefined) setLastWinnerInfo(data.lastWinnerInfo);
                if (data.winningHistory !== undefined) setWinningHistory(data.winningHistory);
            });
            socketRef.current.on('potsUpdate', (data) => { if (data.pots) setPots(data.pots); });
            socketRef.current.on('betUpdate', (data) => { if (data.pots) setPots(data.pots); });
        };
        initAdminSocket();

        // Settings + house stats: poll the backend (was adminSettings/appStats
        // onSnapshot). winningHistory + lastWinnerInfo arrive via the socket above.
        let settingsSeeded = false;
        const loadAdminData = async () => {
            try {
                const [settings, statsRes] = await Promise.all([
                    apiGet(`/api/games/${GAME_KEY}/settings`),
                    apiGet(`/api/games/${GAME_KEY}/stats`),
                ]);
                if (!isMounted) return;
                if (settings) {
                    setIsClockRunning(settings.isClockRunning ?? false);
                    if (!settingsSeeded) {
                        setSelectedBias(settings.biasPercentage ?? 0);
                        setSectionLimit(settings.sectionLimit || 100000);
                        settingsSeeded = true;
                    }
                }
                if (statsRes?.stats) setAppStats(statsRes.stats);
            } catch (e) { /* transient — keep last known values */ }
        };
        loadAdminData();
        const adminPoll = setInterval(loadAdminData, 3000);
        return () => {
            isMounted = false;
            if (socketRef.current) socketRef.current.disconnect();
            if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
            clearInterval(adminPoll);
        };
    }, []);

    useEffect(() => {
        if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
        if (!isClockRunning || !phaseStartTime || !phaseDuration) { setCountdown(0); return; }
        const phaseEndTime = phaseStartTime + (phaseDuration * 1000);
        const tick = () => {
            const now = Date.now();
            const timeLeftMillis = Math.max(0, phaseEndTime - now);
            setCountdown(Math.ceil(timeLeftMillis / 1000));
            if (timeLeftMillis > 0) animationFrameId.current = requestAnimationFrame(tick);
        };
        animationFrameId.current = requestAnimationFrame(tick);
        return () => { if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current); };
    }, [isClockRunning, phaseStartTime, phaseDuration]);

    const handleToggleClock = async () => {
        setLoading(true);
        try { await apiPatch(`/api/games/${GAME_KEY}/settings`, { isClockRunning: !isClockRunning }); } catch (err) { console.error(err); }
        setLoading(false);
    };

    const handleResetRoundId = async () => {
        if (!window.confirm('Round ID 0 করতে চান? Round history, winningHistory আর last winner reset হবে। Undo করা যাবে না।')) return;
        try { await apiPost(`/api/games/${GAME_KEY}/reset-round`); alert('Round ID reset triggered.'); }
        catch (err) { alert('Reset failed: ' + err.message); }
    };

    const handleClearHistory = async () => {
        if (!window.confirm('সব পুরনো dailyGameHistory_Fish delete হবে। নিশ্চিত?')) return;
        try { await apiPost(`/api/games/${GAME_KEY}/clear-history`); alert('Clear History triggered.'); }
        catch (err) { alert('Clear failed: ' + err.message); }
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault(); setBiasLoading(true);
        try {
            await apiPatch(`/api/games/${GAME_KEY}/settings`, { sectionLimit: Number(sectionLimit), biasPercentage: selectedBias });
            alert("Settings Applied Successfully!");
        } catch (err) { alert("Error saving settings"); }
        setBiasLoading(false);
    };

    const getPhaseColor = (phase) => ({ betting: '#3b82f6', showdown: '#f59e0b', victory: '#a78bfa' }[phase] || '#6b7280');
    const getSectionColor = (section) => ({ A: '#ef4444', B: '#f59e0b', C: '#3b82f6' }[section] || '#6b7280');

    return (
        <div className="dark-page">
            {/* Header */}
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Fish Game Control 🐟</h2>
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: isClockRunning ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)', color: isClockRunning ? '#4ade80' : '#f87171', border: `1px solid ${isClockRunning ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}` }}>
                    {isClockRunning ? 'RUNNING' : 'STOPPED'}
                </span>
            </div>

            {/* Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
                {[
                    { label: 'CURRENT PHASE', value: gamePhase.toUpperCase(), color: getPhaseColor(gamePhase) },
                    { label: 'TIMER', value: `${countdown}s`, color: '#f59e0b' },
                    { label: 'ROUND ID', value: roundId, color: '#a78bfa' },
                    { label: 'TOTAL PROFIT', value: formatNumberPrecise(appStats.totalProfit), color: '#4ade80' },
                ].map((s, i) => (
                    <div key={i} className="dark-card" style={{ borderTop: `3px solid ${s.color}`, textAlign: 'center' }}>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700, marginBottom: 8 }}>{s.label}</p>
                        <p style={{ fontSize: i === 1 ? 28 : 22, fontWeight: 900, color: s.color, margin: 0, fontFamily: 'monospace' }}>{s.value}</p>
                    </div>
                ))}
            </div>

            {/* Live Pots */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                {['A', 'B', 'C'].map(section => (
                    <div key={section} className="dark-card" style={{ borderTop: `3px solid ${getSectionColor(section)}`, textAlign: 'center' }}>
                        <p style={{ fontSize: 22, fontWeight: 900, color: getSectionColor(section), marginBottom: 4 }}>POT {section}</p>
                        <p style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{formatNumberPrecise(pots?.[section] || 0)}</p>
                    </div>
                ))}
            </div>

            {/* Settings */}
            <div className="dark-card" style={{ marginBottom: 16 }}>
                <h3 style={{ textAlign: 'center', marginBottom: 20, fontWeight: 700, color: 'rgba(255,255,255,0.7)' }}>Engine Parameters</h3>
                <form onSubmit={handleSaveSettings}>
                    <div style={{ maxWidth: 260, margin: '0 auto 20px', textAlign: 'center' }}>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'rgba(255,255,255,0.7)' }}>Section Payout Limit</label>
                        <input type="number" value={sectionLimit} onChange={e => setSectionLimit(e.target.value)} style={{ width: '100%', padding: '10px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, color: '#f1f5f9', fontSize: 18, textAlign: 'center', fontWeight: 700 }} />
                    </div>
                    <div style={{ textAlign: 'center', marginBottom: 20 }}>
                        <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.7)' }}>Algorithm Bias</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
                            {BIAS_OPTIONS.map(val => (
                                <button key={val} type="button" onClick={() => setSelectedBias(val)} style={{
                                    padding: '8px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13,
                                    background: selectedBias === val ? 'linear-gradient(135deg,#ff4d6d,#ff2d55)' : 'rgba(255,255,255,0.07)',
                                    color: selectedBias === val ? '#fff' : 'rgba(255,255,255,0.6)',
                                    border: selectedBias === val ? 'none' : '1px solid rgba(255,255,255,0.12)', cursor: 'pointer'
                                }}>
                                    {val > 0 ? `+${val}` : val}%
                                </button>
                            ))}
                        </div>
                        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>
                            <b>Minus (-):</b> Profit &nbsp;|&nbsp; <b>Zero (0):</b> Random &nbsp;|&nbsp; <b>Plus (+):</b> Bonus
                        </p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <button type="submit" disabled={biasLoading} className="btn-upload" style={{ padding: '12px 40px', opacity: biasLoading ? 0.5 : 1 }}>
                            {biasLoading ? 'Saving...' : 'APPLY SETTINGS'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Winning History */}
            {winningHistory.length > 0 && (
                <div className="dark-card" style={{ marginBottom: 16 }}>
                    <h3 style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.5)' }}>RECENT HISTORY</h3>
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8 }}>
                        {winningHistory.slice(0, 12).map((entry, idx) => (
                            <div key={idx} style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, background: getSectionColor(entry.section), color: '#fff' }}>
                                {entry.section}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Last Winner */}
            {lastWinnerInfo && (
                <div className="dark-card" style={{ marginBottom: 16, textAlign: 'center', borderTop: '3px solid #818cf8' }}>
                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: 700 }}>LAST WINNER</p>
                    <p style={{ fontSize: 24, fontWeight: 900, color: '#818cf8', margin: '6px 0 0' }}>SECTION {lastWinnerInfo.winnerSection}</p>
                </div>
            )}

            {/* Control Buttons */}
            <div style={{ marginBottom: 12 }}>
                <button onClick={handleToggleClock} disabled={loading} style={{
                    width: '100%', padding: '16px', borderRadius: 12, fontWeight: 900, fontSize: 16, color: '#fff', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                    background: isClockRunning ? 'linear-gradient(135deg,#ef4444,#dc2626)' : 'linear-gradient(135deg,#22c55e,#16a34a)',
                    boxShadow: isClockRunning ? '0 4px 20px rgba(239,68,68,0.4)' : '0 4px 20px rgba(34,197,94,0.4)',
                    opacity: loading ? 0.5 : 1
                }}>
                    {isClockRunning ? 'STOP GAME SERVER' : 'START GAME SERVER'}
                </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                <button onClick={handleResetRoundId} style={{ padding: '12px', borderRadius: 10, fontWeight: 700, color: '#fff', border: 'none', background: 'linear-gradient(135deg,#f97316,#ea580c)', cursor: 'pointer' }}>
                    RESET ROUND ID
                </button>
                <button onClick={handleClearHistory} style={{ padding: '12px', borderRadius: 10, fontWeight: 700, color: '#fff', border: 'none', background: 'linear-gradient(135deg,#e11d48,#be123c)', cursor: 'pointer' }}>
                    CLEAR ALL HISTORY
                </button>
            </div>

            {/* Daily History */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
                <button onClick={() => setShowHistory(!showHistory)} style={{ width: '100%', padding: '12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, fontWeight: 700, color: '#f1f5f9', cursor: 'pointer', fontSize: 14 }}>
                    {showHistory ? 'HIDE DETAILED HISTORY' : 'SHOW DAILY DETAILED HISTORY'}
                </button>
                {showHistory && <div style={{ marginTop: 16 }}><DailyHistoryViewer gameType="trustFish" /></div>}
            </div>
        </div>
    );
};

export default TrustFish;
